import { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Stack,
  Paper,
  Box,
  Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import {
  Save,
  CampaignOutlined,
  DescriptionOutlined,
  AttachFileOutlined,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import axios from "@/api/axios";
import { useSession } from "@/hooks/useSession";
import Grid from "@mui/material/Grid2";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import FileUploadField from "@/components/FileUploadField";
import { useTranslation } from "react-i18next";
import PageHeader from "@/components/PageHeader";

const AnnouncementForm = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { control, handleSubmit, reset, watch, trigger } = useForm();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { token } = useSession();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();
  const [fileNames, setFileNames] = useState([]);

  // Watching values to enable/disable submit button
  const title = watch("title");
  const body = watch("body");

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setFileNames(files.map((file) => file.name));
  };

  const onSubmit = async (data) => {
    const form = new FormData();
    const sentData = {
      title: data.title,
      body: data.body,
    };
    form.append(
      "data",
      new Blob([JSON.stringify(sentData)], { type: "application/json" })
    );
    selectedFiles.forEach((file) => {
      form.append("attachments", file);
    });
    const url = "/api/v1/organizations/announcements";
    try {
      await axios.post(url, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + token,
        },
      });
      setToast({
        open: true,
        severity: "success",
        message: t("announcementForm.successToast"),
      });

      reset();
      setSelectedFiles([]);
      setFileNames([]);
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <>
      {/* Compact Enterprise Header */}
      <PageHeader
        icon={<CampaignOutlined />}
        title={t("announcementForm.headerTitle", {
          defaultValue: "Create New Announcement",
        })}
        subtitle={t("announcementForm.headerSubtitle", {
          defaultValue:
            "Share important updates and information with your organization",
        })}
        t={t}
        translationPrefix="announcementForm"
        compact={true}
      />

      {/* Form Container */}
      <Paper
        elevation={4}
        sx={{
          p: 4,
          width: "85%",
          mx: "auto",
          mb: 4,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
        }}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          encType="multipart/form-data"
          aria-label={t("announcementForm.formAria")}
        >
          {/* Announcement Content Section */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderRadius: 2,
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.2),
              }}
            >
              <DescriptionOutlined
                sx={{ fontSize: 28, color: "primary.main" }}
              />
              <Box>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {t("announcementForm.contentSectionTitle", {
                    defaultValue: "Announcement Content",
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("announcementForm.contentSectionDesc", {
                    defaultValue:
                      "Enter the title and body of your announcement",
                  })}
                </Typography>
              </Box>
            </Box>

            <Stack spacing={3}>
              {/* Title Input */}
              <Controller
                name="title"
                control={control}
                defaultValue=""
                rules={{ required: t("announcementForm.errorTitleRequired") }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("announcementForm.titleLabel")}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onBlur={() => trigger("title")}
                    aria-label={t("announcementForm.titleAria")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
              />

              {/* Body Input */}
              <Controller
                name="body"
                control={control}
                defaultValue=""
                rules={{ required: t("announcementForm.errorBodyRequired") }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("announcementForm.bodyLabel")}
                    fullWidth
                    multiline
                    rows={8}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onBlur={() => trigger("body")}
                    aria-label={t("announcementForm.bodyAria")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
              />
            </Stack>
          </Paper>

          {/* Attachments Section */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
                p: 2,
                bgcolor: alpha(theme.palette.secondary.main, 0.08),
                borderRadius: 2,
                border: "1px solid",
                borderColor: alpha(theme.palette.secondary.main, 0.2),
              }}
            >
              <AttachFileOutlined
                sx={{ fontSize: 28, color: "secondary.main" }}
              />
              <Box>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="secondary.main"
                >
                  {t("announcementForm.attachmentSectionTitle", {
                    defaultValue: "File Attachments",
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("announcementForm.attachmentSectionDesc", {
                    defaultValue:
                      "Add images or documents to support your announcement",
                  })}
                </Typography>
              </Box>
            </Box>

            <Grid size={12}>
              <FileUploadField
                label={t("announcementForm.fileUploadLabel")}
                onChange={handleFileChange}
                fileNames={fileNames}
                inputId="file-upload"
                multiple={true}
                accept="image/*,application/pdf,.doc,.docx,.txt,.zip"
              />
            </Grid>
          </Paper>

          <Divider sx={{ my: 3 }} />

          {/* Submit Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<Save />}
              sx={{
                borderRadius: 3,
                px: 6,
                py: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1.1rem",
                minWidth: 200,
                boxShadow: theme.shadows[4],
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                "&:hover": {
                  boxShadow: theme.shadows[8],
                  transform: "translateY(-2px)",
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
                },
                "&:disabled": {
                  background: alpha(theme.palette.action.disabled, 0.12),
                  color: theme.palette.text.disabled,
                  boxShadow: "none",
                  transform: "none",
                },
                transition: "all 0.3s ease",
              }}
              disabled={!title || !body}
              aria-label={t("announcementForm.submitButtonAria")}
            >
              {t("announcementForm.submitButton")}
            </Button>
          </Box>
        </form>
        <Toast
          open={toast.open}
          onClose={handleToastClose}
          severity={toast.severity}
          message={toast.message}
        />
      </Paper>
    </>
  );
};

export default AnnouncementForm;
